import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { AppShell } from "@/components/layout/AppShell";
import { LoadingState } from "@/components/common/States";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import KartPage from "@/pages/KartPage";
import AssistantPage from "@/pages/AssistantPage";
import LeadsPage from "@/pages/leads/LeadsPage";
import LeadDetailPage from "@/pages/leads/LeadDetailPage";
import CustomersPage from "@/pages/customers/CustomersPage";
import CustomerDetailPage from "@/pages/customers/CustomerDetailPage";
import QuotesPage from "@/pages/quotes/QuotesPage";
import QuoteDetailPage from "@/pages/quotes/QuoteDetailPage";
import ProjectsPage from "@/pages/projects/ProjectsPage";
import ProjectDetailPage from "@/pages/projects/ProjectDetailPage";
import RigDesignPage from "@/pages/projects/RigDesignPage";
import PlanningPage from "@/pages/PlanningPage";
import CapacityPage from "@/pages/planning/CapacityPage";
import AccommodationPage from "@/pages/innkvartering/AccommodationPage";
import EmergencyPage from "@/pages/innkvartering/EmergencyPage";
import ModulesPage from "@/pages/modules/ModulesPage";
import ModuleDetailPage from "@/pages/modules/ModuleDetailPage";
import ServicePage from "@/pages/ServicePage";
import SuppliersPage from "@/pages/SuppliersPage";
import DocumentsPage from "@/pages/DocumentsPage";
import RatesPage from "@/pages/admin/RatesPage";
import UsersPage from "@/pages/admin/UsersPage";
import SettingsPage from "@/pages/admin/SettingsPage";
import FinanceLayout from "@/pages/finance/FinanceLayout";
import FinanceOverviewPage from "@/pages/finance/FinanceOverviewPage";
import InvoicesPage from "@/pages/finance/InvoicesPage";
import InvoiceDetailPage from "@/pages/finance/InvoiceDetailPage";
import ContractsPage from "@/pages/finance/ContractsPage";
import ContractDetailPage from "@/pages/finance/ContractDetailPage";
import CostsPage from "@/pages/finance/CostsPage";
import ProjectEconomicsPage from "@/pages/finance/ProjectEconomicsPage";
import ProjectEconomicsDetailPage from "@/pages/finance/ProjectEconomicsDetailPage";
import ForecastPage from "@/pages/finance/ForecastPage";
import PublicLeadForm from "@/pages/public/PublicLeadForm";
import PublicAssessment from "@/pages/public/PublicAssessment";
import PublicQuotePage from "@/pages/public/PublicQuotePage";
import FieldLayout from "@/pages/field/FieldLayout";
import FieldHome from "@/pages/field/FieldHome";
import FieldProject from "@/pages/field/FieldProject";
import FieldTask from "@/pages/field/FieldTask";
import FieldIssueNew from "@/pages/field/FieldIssueNew";
import FieldTransport from "@/pages/field/FieldTransport";
import FieldTasks from "@/pages/field/FieldTasks";
import PresentationPage from "@/pages/PresentationPage";
import KvitebergOfferPage from "@/pages/public/KvitebergOfferPage";

const Protected = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <LoadingState className="min-h-screen" text="Starter…" />;
  if (!user) return <Navigate to="/login" state={{ from: location.pathname }} replace />;
  return children;
};

const Home = () => {
  const { user } = useAuth();
  if (user?.role === "felt") return <Navigate to="/felt" replace />;
  return <DashboardPage />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/henvendelse" element={<PublicLeadForm />} />
          <Route path="/kartlegging/:token" element={<PublicAssessment />} />
          <Route path="/tilbud/vis/:token" element={<PublicQuotePage />} />
          {/* Tilbod frå Kviteberg AS til Stord Innkvartering (åpen lenke). /tilbud er opptatt av det interne tilbudsmodulen. */}
          <Route path="/tilbud/ki-strategi" element={<KvitebergOfferPage />} />
          {/* Presentasjon – Kviteberg AS for Stord Innkvartering (åpen lenke) */}
          <Route path="/presentasjon" element={<PresentationPage />} />
          <Route path="/presentasjon/:n" element={<PresentationPage />} />
          <Route
            path="/prosjekter/:id/beredskap"
            element={
              <Protected>
                <EmergencyPage />
              </Protected>
            }
          />
          <Route
            path="/felt"
            element={
              <Protected>
                <FieldLayout />
              </Protected>
            }
          >
            <Route index element={<FieldHome />} />
            <Route path="oppgaver" element={<FieldTasks />} />
            <Route path="prosjekt/:id" element={<FieldProject />} />
            <Route path="oppgave/:id" element={<FieldTask />} />
            <Route path="transport" element={<FieldTransport />} />
            <Route path="avvik/ny" element={<FieldIssueNew />} />
          </Route>
          <Route
            path="/"
            element={
              <Protected>
                <AppShell />
              </Protected>
            }
          >
            <Route index element={<Home />} />
            <Route path="henvendelser" element={<LeadsPage />} />
            <Route path="henvendelser/:id" element={<LeadDetailPage />} />
            <Route path="kunder" element={<CustomersPage />} />
            <Route path="kunder/:id" element={<CustomerDetailPage />} />
            <Route path="tilbud" element={<QuotesPage />} />
            <Route path="tilbud/:id" element={<QuoteDetailPage />} />
            <Route path="prosjekter" element={<ProjectsPage />} />
            <Route path="prosjekter/:id" element={<ProjectDetailPage />} />
            <Route path="prosjekter/:id/riggdesign" element={<RigDesignPage />} />
            <Route path="planlegging" element={<PlanningPage />} />
            <Route path="planlegging/kapasitet" element={<CapacityPage />} />
            <Route path="innkvartering" element={<AccommodationPage />} />
            <Route path="kart" element={<KartPage />} />
            <Route path="assistent" element={<AssistantPage />} />
            <Route path="moduler" element={<ModulesPage />} />
            <Route path="moduler/:id" element={<ModuleDetailPage />} />
            <Route path="service" element={<ServicePage />} />
            <Route path="leverandorer" element={<SuppliersPage />} />
            <Route path="leverandorer/:id" element={<SuppliersPage />} />
            <Route path="dokumenter" element={<DocumentsPage />} />
            <Route path="okonomi" element={<FinanceLayout />}>
              <Route index element={<FinanceOverviewPage />} />
              <Route path="fakturaer" element={<InvoicesPage />} />
              <Route path="fakturaer/:id" element={<InvoiceDetailPage />} />
              <Route path="kontrakter" element={<ContractsPage />} />
              <Route path="kontrakter/:id" element={<ContractDetailPage />} />
              <Route path="kostnader" element={<CostsPage />} />
              <Route path="prosjekter" element={<ProjectEconomicsPage />} />
              <Route path="prosjekter/:id" element={<ProjectEconomicsDetailPage />} />
              <Route path="prognose" element={<ForecastPage />} />
            </Route>
            <Route path="admin/priser" element={<RatesPage />} />
            <Route path="admin/brukere" element={<UsersPage />} />
            <Route path="admin/innstillinger" element={<SettingsPage />} />
            <Route path="*" element={<Navigate to="/" replace />} />
          </Route>
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" richColors closeButton />
    </AuthProvider>
  );
}

export default App;
